import { supabase } from "@/integrations/supabase/client";

export interface DeepSearchResult {
  id: string;
  source_type: string;
  source_id: string;
  source_title: string;
  source_url: string | null;
  image_url: string | null;
  thumbnail_url: string | null;
  extracted_text: string;
  enriched_description: string | null;
  category: string | null;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  rank: number;
  headline: string;
}

/**
 * Calls the AI-indexed search backend.
 * @param query   the user's search query (may be empty when filtering by category only)
 * @param type    'photo' | 'document' | null
 * @param limit   max results (default 30, max 60)
 */
export async function deepSearch(
  query: string,
  type: "photo" | "document" | null = null,
  limit = 30
): Promise<DeepSearchResult[]> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-content`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ query, type, limit }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || `Search failed (${response.status})`);
  }

  const data = await response.json();
  return Array.isArray(data?.results) ? (data.results as DeepSearchResult[]) : [];
}

/**
 * Triggers AI re-indexing for a batch of items. Admin-only on the server.
 */
export async function indexContent(items: unknown[]): Promise<{ source_id: string; success: boolean; error?: string }[]> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error("Not authenticated");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/index-content`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || `Indexing failed (${response.status})`);
  }

  const data = await response.json();
  return Array.isArray(data?.results) ? data.results : [];
}
